/**
 * Compute unit (CU) limits per instruction and variant.
 *
 * Each key is an instruction name, and the value is a record of variant names to CU limits.
 * The "default" variant is used when no variant is specified.
 * Validated by SDK compatibility tests.
 */
export const CU_TABLE: Record<string, Record<string, number>> = {
  buy: { default: 300_000, graduation: 400_000 },
  sell: { default: 250_000 },
  // Graduated cp-swap pool swap (swap_base_input): constant-product math +
  // two vault transfers + oracle observation update.
  amm_swap: { default: 350_000 },
  // v2 market-creation lifecycle. Limits match the devnet-bot's working
  // budgets (1_200_000 covers init_outcome's metadata + token+claim mint
  // CPIs; create_market_shell now also creates share metadata).
  create_market_shell: { default: 900_000 },
  init_outcome: { default: 1_200_000 },
  finalize_market: { default: 200_000 },
  init_market: { default: 200_000 },
  init_outcomes: { default: 400_000 },
  init_outcome_metadata: { default: 150_000 },
  init_market_group_mint: { default: 150_000 },
  claim_prize: { default: 150_000 },
  claim_backer_fees: { default: 150_000 },
  resolve_drain_and_migrate: { default: 470_000 },
  graduate: { default: 450_000 },
  distribute_staker_tokens: { default: 200_000 },
  trigger_round: { default: 300_000 },
  challenge_stake: { default: 150_000 },
  boost_market: { default: 150_000 },
  proposal_stake: { default: 150_000 },
  add_thesis: { default: 50_000 },
  claim_revenue_share: { default: 150_000 },
  admin: { default: 100_000 },
};

/**
 * Look up the compute unit limit for an instruction/variant.
 *
 * Returns the CU limit for the given variant, falling back to "default" if no variant
 * is specified, or 200_000 if the instruction is not in the table.
 *
 * @param instruction - Instruction name (e.g. "buy", "sell", "resolve")
 * @param variant - Optional variant (e.g. "graduation"); defaults to "default"
 * @returns Compute unit limit
 */
export function estimateComputeUnits(instruction: string, variant?: string): number {
  const entry = CU_TABLE[instruction];
  if (!entry) {
    return 200_000;
  }
  const key = variant ?? 'default';
  return entry[key] ?? entry.default ?? 200_000;
}
